// Bounded typed endpoint activity used to corroborate observed conversation times.
import { runQuery, rowsToObjects, rangeToTimeRange } from "./analyticsClient.js";
import { parseServerTimestamp } from "./traceUtils.js";
import { withTraceReadSlot } from "./traceReadGate.js";
import { readTelemetryPage, readTelemetryOverview, telemetryField, TELEMETRY_EVENT_KINDS } from "./typedTelemetry.js";
const gatedQuery = (spec, signal) => withTraceReadSlot(signal, () => runQuery(spec, { signal }));
const rfc3339 = (ms) => new Date(ms).toISOString();

// Preserve capture-session attribution while reading typed activity. A session
// is the originating process tree, so an empty session result must not widen to
// unrelated activity elsewhere on the endpoint.
function sessionScope({ sessionId, endpointId, eventTypes }) {
  const types = (eventTypes || []).filter(Boolean).map((type) => {
    const kind = TELEMETRY_EVENT_KINDS[type];
    if (!kind) throw new Error("Unsupported activity type.");
    return kind;
  });
  return [
    { pred: { field: telemetryField("SESSION_ID"), op: "OPERATOR_EQ", value: sessionId } },
    ...(endpointId ? [{ pred: { field: telemetryField("ENDPOINT_ID"), op: "OPERATOR_EQ", value: endpointId } }] : []),
    ...(types.length ? [{ pred: { field: telemetryField("EVENT_KIND"), op: "OPERATOR_IN", value: types } }] : []),
  ];
}

async function readEndpointActivity({ sessionId, endpointId, fromTs, toTs, eventTypes, limit, signal }, details) {
  if (!sessionId || !Number.isFinite(fromTs) || !Number.isFinite(toTs) || toTs <= fromTs) return [];
  const reader = details ? readTelemetryPage : readTelemetryOverview;
  const rows = await reader({
    root: { alias: "e", entity: "ENTITY_TYPE_TELEMETRY_TIMELINE" },
    timeRange: rangeToTimeRange({ from: rfc3339(fromTs), to: rfc3339(toTs) }),
    filter: { andExpr: { children: sessionScope({ sessionId, endpointId, eventTypes }) } },
    orderBy: [{ outputAlias: "ts", direction: "DIRECTION_ASC" }, { outputAlias: "id", direction: "DIRECTION_ASC" }],
    limit,
  }, { details, execute: (spec) => gatedQuery(spec, signal) });
  return rows.map((row) => ({
    id: String(row.id || ""), ts: parseServerTimestamp(row.ts), kind: String(row.eventType || ""),
    ...(details ? { data: row.data, psk: row.psk ?? null } : {}),
  })).filter((event) => Number.isFinite(event.ts));
}

// The overview reads only the narrow activity index. The detail pass hydrates
// selected IDs in bounded family batches for command/file correspondence.
export async function fetchEndpointEvents(args = {}) {
  return readEndpointActivity({ ...args, limit: args.limit ?? 500 }, false);
}

export async function fetchEndpointEventDetails(args = {}) {
  if (!args.eventTypes?.filter(Boolean).length) return [];
  return readEndpointActivity({ ...args, limit: args.limit ?? 200 }, true);
}
